/* eslint-disable react/jsx-props-no-spreading */
import { ComponentProps, FC, useCallback, useEffect, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { createGeneralCall } from '@altermaind/core/requests-status-management';
import { Typography } from '@altermaind/web/ui';
import { useNotification } from '@/_shared/hooks/useNotification';
import { SkeletonTable } from '../SkeletonTable';
import { Paginator } from './Paginator';
import { Table } from './Table';
import { CheckboxesProps, OrderBy, TableProps } from './types';
import { usePaginatedTable } from './usePaginatedTable';

type CreateGeneralCallMustHaveFullRequestSelector = ReturnType<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  typeof createGeneralCall<any, any, any, { [key: string]: any; fullRequest: any }>
>;

type Props = {
  columns: TableProps['columns'];
  pageSize?: number;
  onRowClick: TableProps['onRowClick'];
  onHoverRow?: TableProps['onHoverRow'];
  rowWrapperProps?: TableProps['rowWrapperProps'];
  call: CreateGeneralCallMustHaveFullRequestSelector;
  paginedProps: ReturnType<typeof usePaginatedTable>;
  'header-data-testid'?: string;
  'row-data-testid'?: string;
  loading?: boolean;
  skeletonProps?: ComponentProps<typeof SkeletonTable>;
  'no-data-testid'?: string;
  hasHover?: boolean;
  hidePagination?: boolean;
  defaultOrderBy?: OrderBy;
} & CheckboxesProps;

const DEFAULT_PAGE_SIZE = 30;

export const PaginatedTable: FC<Props> = ({
  columns,
  pageSize = DEFAULT_PAGE_SIZE,
  onRowClick,
  onHoverRow,
  rowWrapperProps = {},
  call,
  paginedProps,
  'header-data-testid': headerDataTestId = 'header-paginated-table',
  'row-data-testid': rowDataTestId = 'row-paginated-table',
  loading = false,
  skeletonProps = {},
  'no-data-testid': noDataTestId = 'no-data-in-table',
  hasHover = false,
  hidePagination = false,
  defaultOrderBy,
  showCheckboxes,
  selectedItems,
  onSelectionChange,
  getRowId,
  bulkActionLabel,
  bulkActionOnClick,
}) => {
  const t = useTranslations();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [internalStart, setInternalStart] = useState(0);
  const { notify } = useNotification();
  const { execute: executeCall, data: rowData, totalItems, fullRequest } = call.hook();
  const [orderBy, setOrderBy] = useState<OrderBy | undefined>(defaultOrderBy);

  const checkboxesProps = {
    showCheckboxes,
    selectedItems,
    onSelectionChange,
    getRowId,
    bulkActionLabel,
    bulkActionOnClick,
  } as CheckboxesProps;

  const {
    paginatedData,
    currentPageNumber,
    goToFirst,
    goToLast,
    lastPageNumber,
    goToPage,
    next,
    previous,
  } = paginedProps;

  const fetchData = useCallback(
    ({ start = 0 }) => {
      const params: object = {
        ...fullRequest?.payload,
        start,
        length: pageSize,
      };
      executeCall({
        ...params,
        errorSaga: () => {
          notify(t('errors.genericApiError'), 'error');
        },
      });
    },
    [pageSize, executeCall, notify, t, fullRequest],
  );

  const handleSort = useCallback(
    (newOrderBy: OrderBy) => {
      setOrderBy(newOrderBy);
      goToFirst();
      const params: object = {
        ...fullRequest?.payload,
        start: 0,
        length: pageSize,
        orderBy: newOrderBy,
      };
      executeCall({
        ...params,
        errorSaga: () => {
          notify(t('errors.genericApiError'), 'error');
        },
      });
    },
    [goToFirst, fullRequest, pageSize, executeCall, notify, t],
  );

  const addData = useCallback(
    (pageNumber: number) => {
      if (currentPageNumber === lastPageNumber && rowData?.length > 0) {
        if (totalItems <= rowData?.length) {
          return;
        }
        setInternalStart(pageSize * (pageNumber - 1));
        fetchData({ start: pageSize * (pageNumber - 1) });
      }
      if (pageNumber && currentPageNumber !== pageNumber && rowData?.length) {
        if (totalItems <= rowData?.length) {
          return;
        }
        setInternalStart(pageSize * (pageNumber - 1));
        fetchData({ start: pageSize * (pageNumber - 1) });
      }
    },
    [pageSize, currentPageNumber, fetchData, lastPageNumber, rowData?.length, totalItems],
  );

  const internalNext = useCallback(() => {
    if (currentPageNumber >= lastPageNumber) return;

    addData(currentPageNumber + 1);
    next();
  }, [addData, next, currentPageNumber, lastPageNumber]);

  const internalPrevius = useCallback(() => {
    addData(currentPageNumber - 1);
    previous();
  }, [addData, previous, currentPageNumber]);

  const internalGoToLast = useCallback(() => {
    if (currentPageNumber >= lastPageNumber) return;

    addData(lastPageNumber);
    goToLast();
  }, [addData, goToLast, currentPageNumber, lastPageNumber]);

  const internalGoToFirst = useCallback(() => {
    const firstPage = 1;
    addData(firstPage);
    goToFirst();
  }, [addData, goToFirst]);

  const internalGoToPage = useCallback(
    (pageNumber: number) => {
      addData(pageNumber);
      goToPage(pageNumber);
    },
    [addData, goToPage],
  );

  useEffect(() => {
    if (currentPageNumber !== 1) {
      if (fullRequest?.payload?.start === 0) {
        goToPage(1);
      }
    }
  }, [currentPageNumber, fullRequest?.payload, goToPage]);

  useEffect(() => {
    if (fullRequest?.payload?.orderBy) {
      setOrderBy(fullRequest.payload.orderBy);
    }
  }, [fullRequest?.payload?.orderBy]);

  if (loading) {
    return <SkeletonTable {...skeletonProps} />;
  }

  return (
    <>
      <Table
        columns={columns}
        rowData={paginatedData}
        onRowClick={onRowClick}
        onHoverRow={onHoverRow}
        rowWrapperProps={rowWrapperProps}
        rowHoverStyle={!hasHover ? { cursor: 'pointer' } : {}}
        cellContentProps={() => {
          return {
            display: 'flex',
          };
        }}
        headerTestId={headerDataTestId}
        rowTestId={rowDataTestId}
        hasHover={hasHover}
        orderBy={orderBy}
        onSort={handleSort}
        {...checkboxesProps}
      />
      {paginatedData.length === 0 && (
        <Typography
          data-testid={noDataTestId}
          textAlign='center'
          variant='p2/light'
          mt='xxl'
          mb='xxl'
        >
          {t('table.noData')}
        </Typography>
      )}
      {!hidePagination && (
        <Paginator
          goToFirst={internalGoToFirst}
          currentPageNumber={currentPageNumber}
          goToLast={internalGoToLast}
          goToPage={internalGoToPage}
          lastPageNumber={lastPageNumber}
          next={internalNext}
          previous={internalPrevius}
        />
      )}
    </>
  );
};
